import React, { useEffect, useRef, useState } from "react";
import { Input, Modal, Typography } from "antd";
import type { InputRef } from "antd";
import { fraseCoincide } from "./botAsistente.utils";

const { Text } = Typography;

/**
 * Confirmación en dos pasos con frase escrita, para lo que no se puede tomar a la ligera (encender el modo real, vaciar
 * las simulaciones). El botón de confirmar sigue apagado hasta que la persona escribe la frase EXACTA; un clic suelto,
 * un Enter distraído o un doble clic no hacen nada hasta entonces.
 *
 * La frase se guarda solo en el estado de este componente: no va a localStorage ni a ningún otro lado, y se borra cada
 * vez que se cierra. Quien llama manda al servidor la frase canónica (no lo escrito), así lo escrito solo sirve de compuerta.
 */
export interface ConfirmacionEscritaProps {
  abierto: boolean;
  titulo: string;
  /** La frase que hay que escribir, tal cual. */
  frase: string;
  textoConfirmar: string;
  /** Pinta el botón de rojo. */
  peligro?: boolean;
  /** Hay una petición en curso: el botón queda en «cargando» y no se puede repetir. */
  cargando?: boolean;
  /**
   * Una razón por la que, por ahora, NO se puede confirmar aunque la frase esté bien escrita (por ejemplo: la última lectura del modo
   * falló y lo que se ve puede ser viejo). Con texto, el botón queda apagado, Enter no confirma y la razón se lee a la vista. Vacío o
   * ausente: se puede confirmar con la frase.
   */
  bloqueadoPor?: string;
  /** Explicación de lo que va a pasar (texto sencillo). */
  children: React.ReactNode;
  onConfirmar: () => void | Promise<void>;
  onCancelar: () => void;
  testId: string;
}

const ConfirmacionEscrita: React.FC<ConfirmacionEscritaProps> = ({
  abierto,
  titulo,
  frase,
  textoConfirmar,
  peligro = true,
  cargando = false,
  bloqueadoPor,
  children,
  onConfirmar,
  onCancelar,
  testId,
}) => {
  const [escrito, setEscrito] = useState("");
  const enviando = useRef(false);
  const campoRef = useRef<InputRef>(null);

  // Cada apertura empieza vacía: una frase escrita antes no se arrastra.
  useEffect(() => {
    if (!abierto) setEscrito("");
  }, [abierto]);

  const coincide = fraseCoincide(escrito, frase);
  const bloqueado = typeof bloqueadoPor === "string" && bloqueadoPor !== "";

  const confirmar = async () => {
    // Doble clic o Enter repetido: la segunda llamada se descarta mientras la primera no termina.
    if (!coincide || cargando || bloqueado || enviando.current) return;
    enviando.current = true;
    try {
      await onConfirmar();
    } finally {
      enviando.current = false;
    }
  };

  return (
    <Modal
      open={abierto}
      title={titulo}
      okText={textoConfirmar}
      cancelText="Cancelar"
      okButtonProps={{ danger: peligro, disabled: !coincide || cargando || bloqueado }}
      confirmLoading={cargando}
      onOk={confirmar}
      onCancel={() => {
        if (!cargando) onCancelar();
      }}
      maskClosable={false}
      // El foco entra al campo de la frase cuando el diálogo ya se ve (con teclado o lector de pantalla lo siguiente es escribirla).
      // `autoFocus` solo no basta: durante la animación de apertura el campo todavía está oculto y el navegador no le da el foco.
      afterOpenChange={(visible) => {
        if (visible) campoRef.current?.focus();
      }}
      destroyOnClose
      style={{ maxWidth: "calc(100vw - 24px)", top: 24 }}
      data-testid={testId}
    >
      <div style={{ fontSize: 14, lineHeight: 1.5 }}>{children}</div>
      <div style={{ marginTop: 14 }}>
        <Text>
          Para confirmar, escribe <Text strong code>{frase}</Text>
        </Text>
        <Input
          ref={campoRef}
          style={{ marginTop: 6 }}
          value={escrito}
          onChange={(e) => setEscrito(e.target.value)}
          onPressEnter={() => void confirmar()}
          placeholder={frase}
          autoComplete="off"
          spellCheck={false}
          // El foco entra al campo de la frase al abrir: con teclado o lector de pantalla, lo siguiente que se hace es
          // escribirla (antes el foco se quedaba en el interruptor que abrió el diálogo, detrás de él).
          autoFocus
          aria-label={`Escribe ${frase} para confirmar`}
          data-testid={`${testId}-frase`}
          status={escrito !== "" && !coincide ? "error" : undefined}
        />
        {escrito !== "" && !coincide && (
          <Text type="danger" style={{ fontSize: 12 }} data-testid={`${testId}-no-coincide`}>
            La frase no coincide. Escríbela con mayúsculas, tal como aparece arriba.
          </Text>
        )}
        {bloqueado && (
          <div style={{ marginTop: 8 }}>
            <Text type="danger" style={{ fontSize: 13 }} data-testid={`${testId}-bloqueado`}>
              {bloqueadoPor}
            </Text>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ConfirmacionEscrita;
